import { useProfile } from "@/context/useProfileProps";
import { customTwMerge as twMerge } from "@/helpers/customTwMerge";
import { TextArea } from "@monsterindia/global-ui-components";
import React, { forwardRef, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import AiAttemptStrip from "@/components/profile/profileSummary/components/aiAttemptStrip";
import SummaryBtn from "@/components/profile/profileSummary/components/summaryBtn";
// import SyncSummary from "@/components/profile/profileSummary/components/syncSummary";
import { useUpdateProfileSummary } from "@/components/profile/profileSummary/query";
import {
  AiSummaryActionType,
  AiSummaryResponse,
  ProfileSummaryFormValues,
  ProfileType,
  SaveSummaryPayloadType,
  SummaryType,
} from "@/components/profile/profileSummary/types";

type ProfileSummaryEditModalProps = {
  closeEdit: (i?: string) => void;
  prefilledData?: ProfileType | null;
  handleAiBtnClick: (type: AiSummaryActionType, prompt?: string) => void;
  aiSummaryData: AiSummaryResponse | null;
  summaryOptions: SummaryType[];
  handleSaveBtnLoading: (v: boolean) => void;
};

const ProfileSummaryEditModal = forwardRef<
  HTMLFormElement,
  ProfileSummaryEditModalProps
>(
  (
    {
      closeEdit,
      prefilledData = null,
      handleAiBtnClick,
      aiSummaryData,
      summaryOptions,
      handleSaveBtnLoading,
    },
    ref,
  ) => {
    const {
      control,
      handleSubmit,
      formState: { errors },
      clearErrors,
      setValue,
      getValues,
    } = useForm<ProfileSummaryFormValues>({
      defaultValues: {
        summary: prefilledData?.title ?? "",
        syncSummary: false,
      },
    });
    const {
      showToast,
      translation: { errorMessages },
      handleEventTracking,
    } = useProfile();
    // const [isShowSyncToggle, setIsShowSyncToggle] = useState(false);
    const { mutate } = useUpdateProfileSummary();

    useEffect(() => {
      if (prefilledData) {
        setValue("summary", prefilledData.title);
      }
    }, [prefilledData]);

    useEffect(() => {
      const syncSummaryToggle = JSON.parse(
        localStorage.getItem("syncSummaryToggle") as string,
      );
      setValue("syncSummary", syncSummaryToggle?.val || false);
      // if (syncSummaryToggle) {
      //   setIsShowSyncToggle(false);
      // } else {
      //   setIsShowSyncToggle(true);
      // }
    }, []);

    const onSubmit = (data: SaveSummaryPayloadType) => {
      handleSaveBtnLoading(true);
      const payload: any = {
        data,
      };
      if (summaryOptions && summaryOptions.length > 0) {
        const insertedSummaryVal = summaryOptions.find(
          (summary) => summary.usedInProfile,
        );

        if (
          insertedSummaryVal &&
          insertedSummaryVal.id !== "current" &&
          insertedSummaryVal.summary === data.summary
        ) {
          const params = {
            summaryId: insertedSummaryVal.id,
          };
          payload.params = params;
        }
      }
      mutate(payload, {
        onSuccess: () => {
          // if (isShowSyncToggle) {
          //   localStorage.setItem(
          //     "syncSummaryToggle",
          //     JSON.stringify({
          //       val: data.syncSummary,
          //     }),
          //   );
          // }
          showToast({
            show: true,
            message: "Profile summary updated successfully",
            type: "success",
          });
          let urlParams = new URLSearchParams(window.location.search);
          const isApp = urlParams.get("app") === "true";
          handleEventTracking({
            eventType: "eventPush",
            eventName: "ProfilePage_Section_Saved",
            eventProps: {
              CV_Headline_Saved: JSON.stringify(data),
              Source: isApp ? "app" : "web",
            },
          });
          handleSaveBtnLoading(false);
          closeEdit("save");
        },
        onError: (err) => {
          showToast({
            show: true,
            message:
              err instanceof Error
                ? err.message
                : errorMessages.somethingWentWrong,
            type: "error",
          });
          handleSaveBtnLoading(false);
        },
      });
    };

    return (
      <form
        ref={ref}
        className="flex h-full flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="relative">
          {aiSummaryData?.isUserEligible && (
            <AiAttemptStrip
              attemptLeft={aiSummaryData.attemptLeft}
              modifier="primary"
            />
          )}
          <Controller
            name="summary"
            control={control}
            rules={{
              minLength: {
                value: 10,
                message: "Minimum 10 characters should be present",
              },
            }}
            render={({ field }) => (
              <TextArea
                {...field}
                id="summary"
                className={`h-48 resize-none ${aiSummaryData?.leftOutAttempt ? "!pb-14" : ""} `}
                placeholder="Write about yourself"
                value={field.value ?? ""}
                maxChars={4000}
                errorText={errors.summary?.message}
              />
            )}
          />
          {aiSummaryData?.leftOutAttempt && (
            <SummaryBtn
              className={twMerge(
                "absolute bottom-3 right-3 -translate-y-1/2",
                errors.summary?.message ? "bottom-8" : "",
              )}
              type="generate"
              handleAiBtnClick={() =>
                handleAiBtnClick("generate", getValues("summary"))
              }
            />
          )}
        </div>
        {/* {isShowSyncToggle && (
          <Controller
            name="syncSummary"
            control={control}
            render={({ field }) => (
              <SyncSummary
                value={field.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setValue("syncSummary", e.target.checked);
                }}
              />
            )}
          />
        )} */}
      </form>
    );
  },
);

export default ProfileSummaryEditModal;
